import React from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Collapse,
  IconButton,
  List,
  ListItem,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import EditPrepCategoriesModal from './EditPrepCategoriesModal'
import CloseIcon from '@mui/icons-material/Close'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import RemoveIcon from '@mui/icons-material/Remove'
import ReplayIcon from '@mui/icons-material/Replay'
import AddIcon from '@mui/icons-material/Add'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import AccessTimeIcon from '@mui/icons-material/AccessTimeRounded'
import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import { useTheme, type Theme } from '@mui/material/styles'
import type { PrepDetailResponse, PrepItemVersion, PrepListItem } from '../../api/prep'
import { useMe } from '../../app/MeContext'
import { getChipColors } from '../../utils/colorByCategory'
import { safeGet, safeSet } from '../../utils/storage'
import { formatRelativeTime } from '../../utils/relativeTime'
import useStationStore from '../../store/station'
import { resolveEpochMs, formatStationTime, resolveStationTimeZone, stationDayDiff } from '../../utils/time'
import usePrepUsageStore from '../../store/prepUsageStore'
import usePrepListStore from '../../store/prepListStore'
import useUserSettingsStore, { clampDrawerFontScale } from '../../store/userSettings'
import { usePrepDrawerStore } from '../../stores/prepDrawerStore'
import useShowClockPrepUsageStore from '../../store/showClockPrepUsageStore'
import PrepFontScaler from './PrepFontScaler'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import {
  capShowClockUsage,
  capShowClockUsageItems,
  formatShowClockUsageItemLabel,
  formatShowClockUsageLabel,
} from '../../utils/showClockUsageLabels'

dayjs.extend(advancedFormat)

export const resolveMarkUsedIconColor = (theme: Theme, used: boolean): string =>
  used ? theme.palette.warning.main : theme.palette.common.white

const pickShortStory = (versions?: PrepItemVersion[]): string => {
  if (!Array.isArray(versions) || versions.length === 0) {
    return ''
  }

  const getBody = (body?: string | null): string => {
    if (typeof body !== 'string') {
      return ''
    }
    return body.trim()
  }

  const neutral = getBody(
    versions.find((version) => version?.form_factor === 'short' && version?.style === 'neutral')?.body,
  )

  if (neutral) {
    return neutral
  }

  const anyShort = getBody(versions.find((version) => version?.form_factor === 'short')?.body)

  if (anyShort) {
    return anyShort
  }

  const medium = getBody(versions.find((version) => version?.form_factor === 'medium')?.body)
  if (medium) {
    return medium
  }

  const long = getBody(versions.find((version) => version?.form_factor === 'long')?.body)
  if (long) {
    return long
  }

  return ''
}

const versionKey = (version: PrepItemVersion): string => {
  const formFactor = version.form_factor ?? ''
  const style = version.style ?? ''
  return `${formFactor}:${style}`
}

export const formatEventDate = (value: string | null | undefined): string | null => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return null
  }

  const [year, month, day] = value.split('-').map(Number)
  const date = new Date(Date.UTC(year, month - 1, day))

  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null
  }

  const weekday = new Intl.DateTimeFormat('en-GB', { weekday: 'long', timeZone: 'UTC' }).format(date)
  const monthName = new Intl.DateTimeFormat('en-GB', { month: 'long', timeZone: 'UTC' }).format(date)

  return `${weekday} ${day} ${monthName} ${year}`
}

const versionLabel = (version: PrepItemVersion): string => {
  const formFactor = version.form_factor ?? 'short'
  const rawLabel = typeof version.label === 'string' ? version.label.trim() : ''
  const rawStyle = typeof version.style === 'string' ? version.style.trim() : ''
  const suppressStyle = rawStyle !== '' && rawStyle.toLowerCase() === 'none'
  const style = rawStyle || 'neutral'
  const baseLabel = rawLabel || formFactor

  if (suppressStyle || style === '') {
    return baseLabel
  }

  return `${baseLabel} (${style})`
}

const normalizeVersion = (version: PrepItemVersion | null | undefined): PrepItemVersion | null => {
  if (!version || typeof version !== 'object') {
    return null
  }

  const formFactor = typeof version.form_factor === 'string' ? version.form_factor : null
  const label = typeof version.label === 'string' ? version.label : null
  const style = typeof version.style === 'string' ? version.style : null
  const body = typeof version.body === 'string' ? version.body : null

  if (!formFactor && !label && !style && !body) {
    return null
  }

  return {
    form_factor: formFactor,
    label,
    style,
    body,
  }
}

const preferredDefaultKey = (versions: PrepItemVersion[]): string => {
  const preferredShort = versions.find(
    (version) => version.form_factor === 'short' && version.style === 'preferred',
  )
  if (preferredShort) {
    return versionKey(preferredShort)
  }

  const neutralShort = versions.find(
    (version) => version.form_factor === 'short' && version.style === 'neutral',
  )
  if (neutralShort) {
    return versionKey(neutralShort)
  }

  const shortAny = versions.find((version) => version.form_factor === 'short')
  if (shortAny) {
    return versionKey(shortAny)
  }

  return versionKey(versions[0])
}

const makeStorageKey = (userId: string | null, prepId: number | string): string => {
  const uid = userId ?? 'anon'
  return `rpa:prep:lastVersion:${uid}:${prepId}`
}

export interface PrepDetailContentProps {
  item: PrepListItem | PrepDetailResponse | null
  loading?: boolean
  error?: string | null
  onClose?: () => void
  onToggleMyPrep?: (next: boolean) => Promise<void>
  onDismiss?: () => Promise<void>
  onMarkUsed?: () => Promise<void>
  onArchive?: () => Promise<void>
  onAddToShowClock?: (params: { prepId: number; prepHeadline: string; launchedFromMyPrep: boolean }) => void
  launchedFromMyPrep?: boolean
  isSuperadmin?: boolean
  containerRole?: 'dialog' | 'region'
  headingId?: string
  onRefreshCategories?: (categoryIds: number[]) => void
}

const PrepDetailContent: React.FC<PrepDetailContentProps> = ({
  item,
  loading = false,
  error = null,
  onClose,
  onToggleMyPrep,
  onDismiss,
  onMarkUsed,
  onArchive,
  onAddToShowClock,
  launchedFromMyPrep = false,
  isSuperadmin = false,
  containerRole = 'region',
  headingId,
  onRefreshCategories,
}) => {
  const [selectedVersionKey, setSelectedVersionKey] = React.useState<string | null>(null)
  const [action, setAction] = React.useState<'favorite' | 'dismiss' | 'archive' | 'used' | null>(null)
  const [usedActionError, setUsedActionError] = React.useState<string | null>(null)
  const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null)
  const [imageOpen, setImageOpen] = React.useState(false)
  const [showFullOriginalText, setShowFullOriginalText] = React.useState(false)
  const [editCategoriesOpen, setEditCategoriesOpen] = React.useState(false)
  const me = useMe()
  const theme = useTheme()
  const closersCollapsed = usePrepDrawerStore((state) => state.collapsed['read.closers'] ?? false)
  const metadataCollapsed = usePrepDrawerStore((state) => state.collapsed['details.metadata'] ?? false)
  const detailsCategoriesCollapsed = usePrepDrawerStore((state) => state.collapsed['details.categories'] ?? false)
  const setSectionCollapsed = usePrepDrawerStore((state) => state.setCollapsed)
  const drawerFontScale = useUserSettingsStore((state) => state.prep.drawerFontScale)
  const setDrawerFontScale = useUserSettingsStore((state) => state.setDrawerFontScale)
  const stationId = useStationStore((state) => state.activeStation?.id ?? 0)
  const stationTimezone = useStationStore((state) => state.activeStation?.timezone ?? null)
  const usageByPrepId = usePrepUsageStore((state) => state.usageByPrepId)
  const fetchUsageFlags = usePrepUsageStore((state) => state.fetchUsageFlags)
  const SCALE_STEP = 0.05
  const SCALE_MIN = 0.85
  const SCALE_MAX = 1.4
  const decreaseDisabled = drawerFontScale <= SCALE_MIN + 0.0001
  const increaseDisabled = drawerFontScale >= SCALE_MAX - 0.0001
  const resetDisabled = Math.abs(drawerFontScale - 1) < 0.0001

  const activeId = item?.id ?? null
  const prepHeadline = React.useMemo(() => {
    const headline = typeof item?.headline === 'string' ? item.headline.trim() : ''
    return headline || 'Prep item'
  }, [item?.headline])
  const myIdsSet = usePrepListStore((state) => state.myIds)

  const ensureShowClockUsage = useShowClockPrepUsageStore((state) => state.ensureUsage)
  const clocksByPrepId = useShowClockPrepUsageStore((state) => state.clocksByPrepId)
  const showClockUsage = React.useMemo(
    () => (activeId ? clocksByPrepId.get(activeId) ?? [] : []),
    [activeId, clocksByPrepId],
  )
  const inLiveOrFutureClock = showClockUsage.length > 0
  const showClockTooltipTitle = React.useMemo(() => {
    if (!inLiveOrFutureClock) {
      return 'Add to Show Clock'
    }
    const { clocks, extraCount } = capShowClockUsage(showClockUsage, 3)
    return (
      <Box sx={{ py: 0.25 }}>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          In live/future show clocks
        </Typography>
        <Box component="div" sx={{ mt: 0.5 }}>
          {clocks.map((clock, index) => {
            const usageItems = Array.isArray(clock.items) ? clock.items : []
            const { items, extraCount: extraItems } = capShowClockUsageItems(usageItems, 3)

            return (
              <Box key={clock.day_id} component="div" sx={{ mt: index === 0 ? 0 : 0.5 }}>
                <Box component="div" sx={{ typography: 'caption', fontWeight: 600 }}>
                  {formatShowClockUsageLabel(clock)}
                </Box>
                {items.map((item) => (
                  <Box key={item.day_item_id} component="div" sx={{ typography: 'caption', ml: 1 }}>
                    {formatShowClockUsageItemLabel(item)}
                  </Box>
                ))}
                {extraItems > 0 ? (
                  <Box component="div" sx={{ typography: 'caption', ml: 1, mt: 0.25 }}>
                    +{extraItems} more
                  </Box>
                ) : null}
              </Box>
            )
          })}
          {extraCount > 0 ? (
            <Box component="div" sx={{ typography: 'caption', mt: 0.25 }}>
              +{extraCount} more
            </Box>
          ) : null}
        </Box>
      </Box>
    )
  }, [inLiveOrFutureClock, showClockUsage])

  React.useEffect(() => {
    if (!stationId || stationId <= 0 || !activeId || activeId <= 0) {
      return
    }
    void ensureShowClockUsage(stationId, [activeId])
  }, [activeId, ensureShowClockUsage, stationId])
  const userId = me?.user?.id != null ? String(me.user.id) : null
  const isStarred = Boolean(item?.is_in_my_prep || (activeId != null && myIdsSet.has(activeId)))

  React.useEffect(() => {
    setUsedActionError(null)
  }, [activeId])

  const versions = React.useMemo(() => {
    const source = item?.versions ?? []
    if (!Array.isArray(source)) {
      return []
    }
    const normalized: PrepItemVersion[] = []
    for (const candidate of source) {
      const parsed = normalizeVersion(candidate)
      if (parsed) {
        normalized.push(parsed)
      }
    }
    return normalized
  }, [item?.versions])

  const versionStorageKey = React.useMemo(() => {
    if (!activeId) {
      return null
    }
    return makeStorageKey(userId, activeId)
  }, [userId, activeId])

  React.useEffect(() => {
    if (!activeId || versions.length === 0) {
      setSelectedVersionKey(null)
      return
    }

    let initialKey: string | null = versionStorageKey ? safeGet(versionStorageKey) : null
    const versionKeys = new Set(versions.map((version) => versionKey(version)))

    if (!initialKey || !versionKeys.has(initialKey)) {
      initialKey = preferredDefaultKey(versions)
    }

    setSelectedVersionKey(initialKey)
  }, [activeId, versions, versionStorageKey])

  const selectedVersion = React.useMemo(() => {
    if (!selectedVersionKey) {
      return null
    }
    return versions.find((version) => versionKey(version) === selectedVersionKey) ?? null
  }, [selectedVersionKey, versions])

  React.useEffect(() => {
    if (!activeId || !stationId) {
      return
    }
    void fetchUsageFlags(stationId, [activeId]).catch(() => undefined)
  }, [activeId, fetchUsageFlags, stationId])

  const categories = item?.categories ?? []
  const sourceUrl = React.useMemo(() => {
    const raw = (item as PrepDetailResponse | null)?.source_url
    if (typeof raw !== 'string') {
      return null
    }
    const trimmed = raw.trim()
    return trimmed.length > 0 ? trimmed : null
  }, [item])
  const originalText = React.useMemo(() => {
    const raw = (item as PrepDetailResponse | null)?.original_text
    if (typeof raw !== 'string') {
      return null
    }
    const trimmed = raw.trim()
    return trimmed.length > 0 ? trimmed : null
  }, [item])
  const publishedEpochMs = React.useMemo(
    () =>
      resolveEpochMs({
        epochSeconds: item?.published_at_epoch ?? undefined,
        isoString: item?.published_at_iso_utc ?? item?.published_at ?? null,
      }),
    [item?.published_at, item?.published_at_epoch, item?.published_at_iso_utc],
  )
  const relative = publishedEpochMs ? formatRelativeTime(publishedEpochMs, stationTimezone) : null
  const shortStory = pickShortStory(item?.versions)
  const storyImage = React.useMemo(() => {
    const rawImage = (item as PrepDetailResponse | null)?.image
    if (!rawImage) {
      return null
    }

    const sizes = rawImage.sizes ?? {}
    const pickSource = (candidates: Array<string | undefined>) =>
      candidates.find((candidate): candidate is string => typeof candidate === 'string' && candidate.trim().length > 0) ??
      null

    const hasDisplaySize = [sizes?.medium, sizes?.large, sizes?.full].some(
      (candidate) => typeof candidate === 'string' && candidate.trim().length > 0,
    )
    if (!hasDisplaySize) {
      return null
    }

    const inlineSrc = pickSource([sizes?.medium, sizes?.large, sizes?.full])
    const modalSrc = pickSource([sizes?.large, sizes?.full, sizes?.medium, sizes?.thumb])

    const src = inlineSrc ?? modalSrc
    if (!src) {
      return null
    }

    const captionRaw = rawImage.caption
    const caption = typeof captionRaw === 'string' && captionRaw.trim().length > 0 ? captionRaw.trim() : null

    return {
      inlineSrc: src,
      modalSrc: modalSrc ?? src,
      alt: typeof rawImage.alt === 'string' ? rawImage.alt : '',
      caption,
    }
  }, [item])
  const hasVersionPills = versions.length > 1
  const prettyPublished = React.useMemo(() => {
    if (typeof publishedEpochMs === 'number') {
      const parsedEpoch = dayjs(publishedEpochMs)
      if (parsedEpoch.isValid()) {
        return parsedEpoch.format('dddd Do MMMM YYYY h:mma')
      }
    }

    const fallbackSource =
      typeof item?.published_at_iso_utc === 'string' && item.published_at_iso_utc.trim().length > 0
        ? item.published_at_iso_utc
        : typeof item?.published_at === 'string' && item.published_at.trim().length > 0
          ? item.published_at
          : null

    if (fallbackSource) {
      const parsed = dayjs(fallbackSource)
      if (parsed.isValid()) {
        return parsed.format('dddd Do MMMM YYYY h:mma')
      }
    }

    return null
  }, [item?.published_at, item?.published_at_iso_utc, publishedEpochMs])
  const prettyEventDate = React.useMemo(() => formatEventDate(item?.event_date), [item?.event_date])
  const prominentDate = prettyEventDate
    ? `For ${prettyEventDate}`
    : prettyPublished
      ? `Published ${prettyPublished}`
      : null
  const primaryCategory = React.useMemo(() => {
    if (categories.length > 0) {
      return categories[0]
    }
    return item?.primary_category ?? null
  }, [categories, item?.primary_category])
  const primaryChipColors = React.useMemo(
    () => (primaryCategory ? getChipColors(primaryCategory.slug, { selected: true }) : null),
    [primaryCategory],
  )
  const showMetaRow = Boolean(prominentDate)
  const imageDialogTitleId = React.useMemo(
    () => (item?.id != null ? `prep-detail-image-dialog-${item.id}` : 'prep-detail-image-dialog'),
    [item?.id],
  )
  const storyBody = selectedVersion?.body ?? shortStory
  const teaseText = typeof item?.tease === 'string' ? item.tease.trim() : ''
  const questionText = (() => {
    const raw = (item as { question?: string | null } | null)?.question
    return typeof raw === 'string' ? raw.trim() : ''
  })()
  const hookEntries = React.useMemo(
    () =>
      [
        teaseText
          ? ({ key: 'tease', label: 'Tease', value: teaseText } as const)
          : null,
        questionText
          ? ({ key: 'question', label: 'Question', value: questionText } as const)
          : null,
      ].filter(Boolean) as Array<{ key: 'tease' | 'question'; label: string; value: string }>,
    [questionText, teaseText],
  )
  const closers = item?.closers ?? []
  const rawNotes = (item as { notes?: string | null } | null)?.notes ?? null
  const trimmedNotes = typeof rawNotes === 'string' ? rawNotes.trim() : ''
  const notesAvailable = trimmedNotes.length > 0
  const shouldCollapseOriginalText = Boolean(originalText && originalText.length > 600)
  const showOriginalTextExpanded = !shouldCollapseOriginalText || showFullOriginalText
  const showSourceSection = Boolean(sourceUrl || originalText)

  React.useEffect(() => {
    setShowFullOriginalText(false)
  }, [item?.id])

  const metadataEntries = React.useMemo(
    () => {
      if (!item) {
        return [] as Array<{ key: string; label: string; value: React.ReactNode; tooltip?: string }>
      }
      const entries: Array<{ key: string; label: string; value: React.ReactNode; tooltip?: string }> = []
      if (prettyEventDate) {
        entries.push({ key: 'event-date', label: 'Event date', value: prettyEventDate })
      }
      if (relative) {
        entries.push({
          key: 'published',
          label: 'Published',
          value: relative.label,
          tooltip: relative.tooltip ?? relative.label,
        })
      }
      entries.push({ key: 'id', label: 'Prep ID', value: item.id })
      if (item.primary_category?.name) {
        entries.push({
          key: 'primary-category',
          label: 'Primary category',
          value: item.primary_category.name,
        })
      }
      return entries
    },
    [item, prettyEventDate, relative],
  )

  const handleToggleClosers = React.useCallback(() => {
    setSectionCollapsed('read.closers', !closersCollapsed)
  }, [closersCollapsed, setSectionCollapsed])

  const handleToggleMetadata = React.useCallback(() => {
    setSectionCollapsed('details.metadata', !metadataCollapsed)
  }, [metadataCollapsed, setSectionCollapsed])

  const handleToggleDetailsCategories = React.useCallback(() => {
    setSectionCollapsed('details.categories', !detailsCategoriesCollapsed)
  }, [detailsCategoriesCollapsed, setSectionCollapsed])

  const handleSelectVersion = React.useCallback(
    (key: string) => {
      setSelectedVersionKey(key)
      if (versionStorageKey) {
        safeSet(versionStorageKey, key)
      }
    },
    [versionStorageKey],
  )

  const handleDecreaseFontScale = React.useCallback(() => {
    setDrawerFontScale(clampDrawerFontScale(drawerFontScale - SCALE_STEP))
  }, [drawerFontScale, setDrawerFontScale])

  const handleIncreaseFontScale = React.useCallback(() => {
    setDrawerFontScale(clampDrawerFontScale(drawerFontScale + SCALE_STEP))
  }, [drawerFontScale, setDrawerFontScale])

  const handleResetFontScale = React.useCallback(() => {
    setDrawerFontScale(1)
  }, [setDrawerFontScale])

  const handleToggleMyPrep = async () => {
    if (!onToggleMyPrep || !item) {
      return
    }
    if (action) {
      return
    }
    setAction('favorite')
    try {
      await onToggleMyPrep(!isStarred)
    } finally {
      setAction(null)
    }
  }

  const handleDismiss = async () => {
    if (!onDismiss) {
      return
    }
    if (action) {
      return
    }
    setAction('dismiss')
    try {
      await onDismiss()
      if (onClose) {
        onClose()
      }
    } finally {
      setAction(null)
    }
  }

  const handleArchive = async () => {
    if (!onArchive) {
      return
    }
    if (action) {
      return
    }
    setAction('archive')
    try {
      await onArchive()
      setMenuAnchor(null)
      if (onClose) {
        onClose()
      }
    } finally {
      setAction(null)
    }
  }

  const openMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchor(event.currentTarget)
  }

  const closeMenu = () => {
    setMenuAnchor(null)
  }

  const openImage = () => {
    setImageOpen(true)
  }

  const closeImage = () => {
    setImageOpen(false)
  }

  React.useEffect(() => {
    setImageOpen(false)
  }, [item?.id])

  const handleClose = () => {
    if (onClose) {
      onClose()
    }
  }

  const usage = activeId ? usageByPrepId.get(activeId) : undefined
  const rawUsedAt = usage?.used_at ? usage.used_at.trim() : ''
  const usedAtEpoch = React.useMemo(() => {
    if (!rawUsedAt) {
      return null
    }
    // Stored as UTC; append Z so parse doesn't assume local time.
    const isoUtc = `${rawUsedAt.replace(' ', 'T')}Z`
    const parsed = Date.parse(isoUtc)
    if (Number.isFinite(parsed)) {
      return parsed
    }
    const fallback = resolveEpochMs({ isoString: rawUsedAt })
    return fallback
  }, [rawUsedAt])
  const usedActor =
    (usage?.used_by_display_name && usage.used_by_display_name.trim()) ||
    (usage?.used_by_user_id ? 'another presenter' : null)
  const usedPills = React.useMemo(() => {
    if (!usage?.used) {
      return []
    }
    const entries = Array.isArray(usage.usages) && usage.usages.length > 0
      ? usage.usages
      : [
          {
            used_at: usage.used_at ?? null,
            user_id: usage.used_by_user_id ?? null,
            used_by_display_name: usedActor ?? null,
          },
        ]
    const tz = resolveStationTimeZone(stationTimezone)
    return entries.map((entry, index) => {
      const who =
        (entry.used_by_display_name && entry.used_by_display_name.trim()) ||
        (entry.user_id ? 'another presenter' : null) ||
        'another presenter'
      const rawAt = typeof entry.used_at === 'string' ? entry.used_at.trim() : ''
      const epoch = rawAt ? Date.parse(`${rawAt.replace(' ', 'T')}Z`) : null
      let whenLabel = 'time unknown'
      if (Number.isFinite(epoch)) {
        const timeLabel = formatStationTime(epoch as number, tz, {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        })
        const dayDiff = stationDayDiff(Date.now(), epoch as number, tz)
        if (dayDiff === 0) {
          whenLabel = `today at ${timeLabel}`
        } else if (dayDiff === 1) {
          whenLabel = `yesterday at ${timeLabel}`
        } else {
          const dateLabel = formatStationTime(epoch as number, tz, {
            weekday: 'long',
            day: 'numeric',
          })
          whenLabel = `${dateLabel} at ${timeLabel}`
        }
      } else if (rawAt) {
        whenLabel = rawAt
      }

      return {
        key: `${entry.user_id ?? 'u'}-${index}-${rawAt}`,
        text: `This prep item was used by ${who} ${whenLabel}`,
      }
    })
  }, [stationTimezone, usage, usedActor])

  const handleMarkUsed = async () => {
    if (!item?.id || !stationId || !onMarkUsed || action) {
      return
    }
    setUsedActionError(null)
    setAction('used')
    try {
      await onMarkUsed()
    } catch (error_) {
      const message =
        error_ && typeof error_ === 'object' && 'message' in error_
          ? String((error_ as { message?: unknown }).message ?? 'Unable to mark as used.')
          : 'Unable to mark as used.'
      setUsedActionError(message)
    } finally {
      setAction(null)
    }
  }

  if (!item && !loading && !error) {
    return null
  }

  return (
    <PrepFontScaler theme={theme} scale={drawerFontScale}>
      <Box
        role={containerRole}
        aria-labelledby={headingId}
        sx={{ width: '100%', boxSizing: 'border-box' }}
      >
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            pt: 1.5,
            pb: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Typography
              id={headingId}
              variant="h6"
              sx={{ pr: 2, lineHeight: 1.15, mt: 0, mb: 0 }}
            >
              {item?.headline ?? 'Prep detail'}
            </Typography>
            {onClose ? (
              <IconButton aria-label="Close" onClick={handleClose} size="small">
                <CloseIcon />
              </IconButton>
            ) : null}
          </Box>

          {loading && !item ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : null}

          {error ? (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {error}
            </Alert>
          ) : null}

          {usedActionError ? (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {usedActionError}
            </Alert>
          ) : null}

          {item ? (
            <Stack spacing={2} sx={{ mt: 1.5 }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: storyImage ? '1fr auto' : '1fr',
                  },
                  alignItems: 'start',
                  columnGap: 3,
                  rowGap: { xs: 2, md: 1 },
                  mb: 0,
                }}
              >
                <Stack
                  spacing={1.25}
                  sx={{
                    gridColumn: { xs: '1 / -1', md: '1 / 2' },
                    m: 0,
                    minWidth: 0,
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={2}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ m: 0 }}
                  >
                    {primaryCategory && primaryChipColors ? (
                      <Chip
                        label={primaryCategory.name}
                        size="small"
                        sx={{
                          borderRadius: 999,
                          fontWeight: 600,
                          height: 24,
                          borderWidth: 1,
                          borderStyle: 'solid',
                          backgroundColor: primaryChipColors.background,
                          borderColor: primaryChipColors.border,
                          color: primaryChipColors.text,
                        }}
                      />
                    ) : null}
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ gap: 1 }}>
                      <Tooltip title="Mark as used" enterDelay={300}>
                        <span>
                          <IconButton
                            aria-label="Mark as used"
                            onClick={handleMarkUsed}
                            disabled={action !== null || !stationId || !onMarkUsed}
                            size="small"
                            sx={{ color: (theme) => resolveMarkUsedIconColor(theme, Boolean(usage?.used)) }}
                          >
                            <CheckCircleOutlineIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={isStarred ? 'Remove from My Prep' : 'Add to My Prep'}
                        enterDelay={300}
                      >
                        <span>
                          <IconButton
                            aria-label={isStarred ? 'Remove from My Prep' : 'Add to My Prep'}
                            onClick={handleToggleMyPrep}
                            disabled={action !== null || !onToggleMyPrep}
                            size="small"
                          >
                            {isStarred ? <StarIcon color="warning" /> : <StarBorderIcon />}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Dismiss for me" enterDelay={300}>
                        <span>
                          <IconButton
                            aria-label="Dismiss for me"
                            onClick={handleDismiss}
                            disabled={action !== null}
                            size="small"
                          >
                            <VisibilityOffIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={showClockTooltipTitle} enterDelay={300}>
                        <span>
                          <IconButton
                            aria-label="Add to Show Clock"
                            onClick={() => {
                              if (!onAddToShowClock || !stationId || !activeId) {
                                return
                              }
                              onAddToShowClock({ prepId: activeId, prepHeadline, launchedFromMyPrep })
                            }}
                            disabled={action !== null || !onAddToShowClock || !stationId || !activeId}
                            size="small"
                            color={inLiveOrFutureClock ? 'success' : 'default'}
                          >
                            <AccessTimeIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {isSuperadmin && onArchive ? (
                        <>
                          <Tooltip title="More actions" enterDelay={300}>
                            <span>
                              <IconButton
                                aria-label="More actions"
                                onClick={openMenu}
                                disabled={action !== null}
                                size="small"
                                aria-haspopup="menu"
                                aria-expanded={Boolean(menuAnchor)}
                              >
                                <MoreVertIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Menu
                            anchorEl={menuAnchor}
                            open={Boolean(menuAnchor)}
                            onClose={closeMenu}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                          >
                            <MenuItem onClick={() => void handleArchive()}>
                              Archive for everyone
                            </MenuItem>
                            <MenuItem
                              onClick={() => {
                                closeMenu()
                                setEditCategoriesOpen(true)
                              }}
                            >
                              Edit category
                            </MenuItem>
                          </Menu>
                        </>
                      ) : null}
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Tooltip title="Decrease font size">
                        <span>
                          <IconButton
                            aria-label="Decrease font size"
                            size="small"
                            onClick={handleDecreaseFontScale}
                            disabled={decreaseDisabled}
                          >
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Reset font size">
                        <span>
                          <IconButton
                            aria-label="Reset font size"
                            size="small"
                            onClick={handleResetFontScale}
                            disabled={resetDisabled}
                          >
                            <ReplayIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Increase font size">
                        <span>
                          <IconButton
                            aria-label="Increase font size"
                            size="small"
                            onClick={handleIncreaseFontScale}
                            disabled={increaseDisabled}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Stack>
                  {usedPills.length > 0 ? (
                    <Stack spacing={1} sx={{ mt: 1.25 }}>
                      {usedPills.map((pill) => (
                        <Box
                          key={pill.key}
                          sx={(theme) => ({
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 1.5,
                            py: 0.75,
                            borderRadius: 999,
                            backgroundColor: theme.palette.warning.main,
                            color: theme.palette.warning.contrastText,
                            fontWeight: 600,
                          })}
                          role="status"
                          aria-label={pill.text}
                        >
                          <CheckCircleOutlineIcon fontSize="small" />
                          <span>{pill.text}</span>
                        </Box>
                      ))}
                    </Stack>
                  ) : null}
                  {showMetaRow ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        rowGap: 0.5,
                        m: 0,
                      }}
                    >
                      {prominentDate ? (
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                          }}
                        >
                          {prominentDate}
                        </Typography>
                      ) : null}
                    </Box>
                  ) : null}
                  {hookEntries.length > 0 ? (
                    <Stack spacing={1.5} sx={{ m: 0 }}>
                      {hookEntries.map((entry) => (
                        <Box key={entry.key} sx={{ m: 0 }}>
                          <Chip
                            label={entry.label}
                            size="small"
                            sx={{
                              borderRadius: 999,
                              fontWeight: 600,
                              height: 24,
                            }}
                          />
                          <Typography
                            variant="body1"
                            sx={{ mt: 1, whiteSpace: 'pre-wrap' }}
                          >
                            {entry.value}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  ) : null}
                </Stack>
                {storyImage ? (
                  <Box
                    sx={{
                      gridColumn: { xs: '1 / -1', md: '2 / 3' },
                      justifySelf: { xs: 'start', md: 'end' },
                      position: 'relative',
                      width: 250,
                      height: 250,
                      borderRadius: '5px',
                      overflow: 'hidden',
                      boxShadow: (theme) => theme.shadows[2],
                      transition: 'transform 160ms ease, box-shadow 160ms ease',
                      '&:hover': {
                        transform: 'scale(1.02)',
                        boxShadow: (theme) => theme.shadows[4],
                      },
                      '&:focus-within': {
                        transform: 'scale(1.02)',
                        boxShadow: (theme) => theme.shadows[5],
                      },
                      '&:hover .openImageBtn, &:focus-within .openImageBtn': { opacity: 1 },
                    }}
                  >
                    <Box
                      component="img"
                      src={storyImage.inlineSrc}
                      alt={storyImage.alt}
                      tabIndex={0}
                      onClick={openImage}
                      title={storyImage.caption ?? undefined}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        cursor: 'pointer',
                        transition: 'transform 160ms ease',
                        '&:focus-visible': {
                          outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                          outlineOffset: 2,
                        },
                      }}
                    />
                    <IconButton
                      aria-label="Open image"
                      onClick={openImage}
                      size="small"
                      className="openImageBtn"
                      sx={{
                        position: 'absolute',
                        right: 8,
                        bottom: 8,
                        bgcolor: 'rgba(0,0,0,0.55)',
                        color: '#fff',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                        boxShadow: 2,
                        opacity: { xs: 1, md: 0 },
                        transition: 'opacity 160ms ease',
                      }}
                    >
                      <ZoomInIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : null}
              </Box>

              <Stack spacing={2} sx={{ mt: { xs: 1, md: 1.25 } }}>
                <Stack spacing={1.5}>
                  {storyBody ? (
                    <Box>
                      {hasVersionPills ? (
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ flexWrap: 'wrap', rowGap: 1, mb: 1 }}
                          aria-label="Story versions"
                          role="toolbar"
                        >
                          {versions.map((version) => {
                            const key = versionKey(version)
                            const label = versionLabel(version)
                            const isSelected = key === selectedVersionKey
                            const button = (
                              <Button
                                key={key}
                                type="button"
                                variant={isSelected ? 'contained' : 'outlined'}
                                color="primary"
                                size="small"
                                onClick={() => handleSelectVersion(key)}
                                aria-pressed={isSelected}
                                sx={{
                                  borderRadius: 999,
                                  textTransform: 'none',
                                  maxWidth: 220,
                                  px: 1.5,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {label}
                              </Button>
                            )
                            return (
                              <Tooltip
                                key={key}
                                title={label}
                                disableHoverListener={label.length < 18}
                                disableFocusListener={label.length < 18}
                                disableTouchListener={label.length < 18}
                              >
                                {button}
                              </Tooltip>
                            )
                          })}
                        </Stack>
                      ) : null}
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {storyBody}
                      </Typography>
                    </Box>
                  ) : null}
                </Stack>

                {closers.length > 0 ? (
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Closers
                      </Typography>
                      <Tooltip title={closersCollapsed ? 'Expand section' : 'Collapse section'}>
                        <IconButton
                          aria-label={closersCollapsed ? 'Expand closers' : 'Collapse closers'}
                          size="small"
                          onClick={handleToggleClosers}
                          aria-expanded={!closersCollapsed}
                          aria-controls="prep-detail-closers"
                        >
                          <ExpandMoreIcon
                            fontSize="small"
                            sx={{
                              transform: closersCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                              transition: 'transform 150ms ease',
                            }}
                          />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    <Collapse in={!closersCollapsed} timeout="auto">
                      <List dense disablePadding id="prep-detail-closers" sx={{ mt: 1 }}>
                        {closers.map((raw, idx) => {
                          const [mainRaw, altRaw] = String(raw).split('[ALT]')
                          const main = mainRaw?.trim()
                          const alt = altRaw?.trim()

                          return (
                            <ListItem key={idx} disableGutters sx={{ display: 'block', pl: 0, mb: alt ? 1 : 0.5 }}>
                              {main ? (
                                <Typography variant="body2" sx={{ mb: alt ? 0.5 : 0 }}>
                                  {main}
                                </Typography>
                              ) : null}
                              {alt ? (
                                <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.9 }}>
                                  Alt: {alt}
                                </Typography>
                              ) : null}
                            </ListItem>
                          )
                        })}
                      </List>
                    </Collapse>
                  </Box>
                ) : null}

                {metadataEntries.length > 0 ? (
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Metadata
                      </Typography>
                      <Tooltip title={metadataCollapsed ? 'Expand section' : 'Collapse section'}>
                        <IconButton
                          aria-label={metadataCollapsed ? 'Expand metadata' : 'Collapse metadata'}
                          size="small"
                          onClick={handleToggleMetadata}
                          aria-expanded={!metadataCollapsed}
                          aria-controls="prep-detail-metadata"
                        >
                          <ExpandMoreIcon
                            fontSize="small"
                            sx={{
                              transform: metadataCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                              transition: 'transform 150ms ease',
                            }}
                          />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    <Collapse in={!metadataCollapsed} timeout="auto">
                      <List dense disablePadding id="prep-detail-metadata" sx={{ mt: 1 }}>
                        {metadataEntries.map((entry) => {
                          const valueNode = (
                            <Typography variant="body2" component="span">
                              {entry.value}
                            </Typography>
                          )
                          return (
                            <ListItem key={entry.key} disableGutters sx={{ pl: 0, mb: 1 }}>
                              <Stack spacing={0.25}>
                                <Typography variant="caption" color="text.secondary">
                                  {entry.label}
                                </Typography>
                                {entry.tooltip ? (
                                  <Tooltip title={entry.tooltip}>
                                    <span>{valueNode}</span>
                                  </Tooltip>
                                ) : (
                                  valueNode
                                )}
                              </Stack>
                            </ListItem>
                          )
                        })}
                      </List>
                    </Collapse>
                  </Box>
                ) : null}

                {categories.length > 0 ? (
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Categories
                      </Typography>
                      <Tooltip title={detailsCategoriesCollapsed ? 'Expand section' : 'Collapse section'}>
                        <IconButton
                          aria-label={detailsCategoriesCollapsed ? 'Expand categories' : 'Collapse categories'}
                          size="small"
                          onClick={handleToggleDetailsCategories}
                          aria-expanded={!detailsCategoriesCollapsed}
                          aria-controls="prep-detail-categories"
                        >
                          <ExpandMoreIcon
                            fontSize="small"
                            sx={{
                              transform: detailsCategoriesCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                              transition: 'transform 150ms ease',
                            }}
                          />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    <Collapse in={!detailsCategoriesCollapsed} timeout="auto">
                      <Stack
                        id="prep-detail-categories"
                        direction="row"
                        spacing={1}
                        sx={{ flexWrap: 'wrap', rowGap: 1, mt: 1 }}
                      >
                        {categories.map((category) => {
                          const chipColors = getChipColors(category.slug, { selected: true })
                          return (
                            <Chip
                              key={`details-${category.slug}`}
                              label={category.name}
                              size="small"
                              sx={{
                                borderRadius: 999,
                                fontWeight: 600,
                                height: 26,
                                borderWidth: 1,
                                borderStyle: 'solid',
                                backgroundColor: chipColors.background,
                                borderColor: chipColors.border,
                                color: chipColors.text,
                              }}
                            />
                          )
                        })}
                      </Stack>
                    </Collapse>
                  </Box>
                ) : null}

                {notesAvailable ? (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                      Notes
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {trimmedNotes}
                    </Typography>
                  </Box>
                ) : null}

                {showSourceSection ? (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Source
                    </Typography>
                    {sourceUrl ? (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        <Box
                          component="a"
                          href={sourceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={sourceUrl}
                          sx={{
                            color: 'primary.main',
                            textDecoration: 'underline',
                            display: 'inline-block',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {sourceUrl}
                        </Box>
                      </Typography>
                    ) : null}
                    {originalText ? (
                      <>
                        <Typography
                          component="pre"
                          variant="body2"
                          sx={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            mt: 1,
                            ...(shouldCollapseOriginalText && !showOriginalTextExpanded
                              ? {
                                  display: '-webkit-box',
                                  WebkitBoxOrient: 'vertical',
                                  WebkitLineClamp: 10,
                                  overflow: 'hidden',
                                }
                              : {}),
                          }}
                        >
                          {originalText}
                        </Typography>
                        {shouldCollapseOriginalText ? (
                          <Button
                            type="button"
                            variant="text"
                            size="small"
                            onClick={() => setShowFullOriginalText((prev) => !prev)}
                            sx={{ mt: 0.5, textTransform: 'none', px: 0 }}
                          >
                            {showOriginalTextExpanded ? 'Show less' : 'Show more'}
                          </Button>
                        ) : null}
                      </>
                    ) : null}
                  </Box>
                ) : null}
              </Stack>
            </Stack>
          ) : null}
        </Box>
      </Box>

      {storyImage ? (
        <Dialog
          open={imageOpen}
          onClose={closeImage}
          maxWidth="lg"
          aria-labelledby={imageDialogTitleId}
        >
          <DialogTitle id={imageDialogTitleId} sx={{ pr: 6 }}>
            Image preview
            <IconButton
              aria-label="Close image preview"
              onClick={closeImage}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Box
              component="img"
              src={storyImage.modalSrc}
              alt={storyImage.alt}
              sx={{
                maxWidth: '90vw',
                maxHeight: '85vh',
                width: '100%',
                height: 'auto',
                objectFit: 'contain',
                borderRadius: '5px',
              }}
            />
            <Typography variant="caption" sx={{ textAlign: 'center' }}>
              {item?.headline ?? ''}
            </Typography>
          </DialogContent>
        </Dialog>
      ) : null}
      <EditPrepCategoriesModal
        open={editCategoriesOpen && Boolean(item)}
        item={item as PrepListItem}
        onClose={() => setEditCategoriesOpen(false)}
        onSaved={(updated) => {
          setEditCategoriesOpen(false)
          if (onRefreshCategories) {
            const newIds =
              Array.isArray(updated.categories) && updated.categories.length > 0
                ? updated.categories
                    .map((entry) => (entry && typeof entry.id === 'number' ? Math.floor(entry.id) : null))
                    .filter((id): id is number => id !== null)
                : []
            const oldIds =
              Array.isArray(item?.categories) && (item?.categories?.length ?? 0) > 0
                ? (item?.categories as PrepCategoryWithCount[])
                    .map((entry) => (entry && typeof entry.id === 'number' ? Math.floor(entry.id) : null))
                    .filter((id): id is number => id !== null)
                : []
            const allIds = Array.from(new Set([...oldIds, ...newIds]))
            onRefreshCategories(allIds.length ? allIds : newIds)
          }
        }}
      />
    </PrepFontScaler>
  )

}

export default PrepDetailContent
